"use client";

import * as React from "react";

import { type Editor, isNodeSelection } from "@tiptap/react";

// --- Icons ---
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBold,
  faCode,
  faItalic,
  faStrikethrough,
  faSubscript,
  faSuperscript,
  faUnderline,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
// --- UI Primitives ---
import {
  UnifiedButton,
  UnifiedButtonProps,
} from "@/components/tiptap-ui/unified-button";
// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
// --- Lib ---
import { isMarkInSchema } from "@/lib/tiptap-utils";

type Mark =
  | "bold"
  | "italic"
  | "strike"
  | "code"
  | "underline"
  | "superscript"
  | "subscript";

interface MarkButtonProps extends Omit<UnifiedButtonProps, "type"> {
  /**
   * The type of mark to toggle
   */
  type: Mark;
  /**
   * Optional editor instance. If not provided, will use editor from context
   */
  editor?: Editor | null;
  /**
   * Display text for the button (optional)
   */
  text?: string;
  /**
   * Whether this button should be hidden when the mark is not available
   */
  hideWhenUnavailable?: boolean;
}

const markIcons = {
  bold: faBold,
  italic: faItalic,
  underline: faUnderline,
  strike: faStrikethrough,
  code: faCode,
  superscript: faSuperscript,
  subscript: faSubscript,
};

const markShortcutKeys: Partial<Record<Mark, string>> = {
  bold: "Ctrl-b",
  italic: "Ctrl-i",
  underline: "Ctrl-u",
  strike: "Ctrl-Shift-s",
  code: "Ctrl-e",
  superscript: "Ctrl-.",
  subscript: "Ctrl-,",
};

function canToggleMark(editor: Editor | null, type: Mark): boolean {
  if (!editor) return false;

  try {
    return editor.can().toggleMark(type);
  } catch {
    return false;
  }
}

function isMarkActive(editor: Editor | null, type: Mark): boolean {
  if (!editor) return false;
  return editor.isActive(type);
}

function toggleMark(editor: Editor | null, type: Mark): void {
  if (!editor) return;
  editor.chain().focus().toggleMark(type).run();
}

function isMarkButtonDisabled(
  editor: Editor | null,
  type: Mark,
  userDisabled: boolean = false,
): boolean {
  if (!editor) return true;
  if (userDisabled) return true;
  if (editor.isActive("codeBlock")) return true;
  if (!canToggleMark(editor, type)) return true;
  return false;
}

function shouldShowMarkButton(params: {
  editor: Editor | null;
  type: Mark;
  hideWhenUnavailable: boolean;
  markInSchema: boolean;
}): boolean {
  const { editor, type, hideWhenUnavailable, markInSchema } = params;

  if (!markInSchema) {
    return false;
  }

  if (hideWhenUnavailable) {
    if (
      isNodeSelection(editor?.state.selection) ||
      !canToggleMark(editor, type)
    ) {
      return false;
    }
  }

  return true;
}

function getFormattedMarkName(type: Mark): string {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function useMarkState(
  editor: Editor | null,
  type: Mark,
  disabled: boolean = false,
) {
  const markInSchema = isMarkInSchema(type, editor);
  const isDisabled = isMarkButtonDisabled(editor, type, disabled);
  const isActive = isMarkActive(editor, type);

  const iconDefinition = markIcons[type];
  const shortcutKey = markShortcutKeys[type];
  const formattedName = getFormattedMarkName(type);

  return {
    markInSchema,
    isDisabled,
    isActive,
    iconDefinition,
    shortcutKey,
    formattedName,
  };
}

export const MarkButton = React.forwardRef<HTMLButtonElement, MarkButtonProps>(
  (
    {
      editor: providedEditor,
      type,
      text,
      hideWhenUnavailable = false,
      className = "",
      disabled,
      onClick,
      children,
      ...buttonProps
    }: MarkButtonProps,
    ref,
  ) => {
    const editor = useTiptapEditor(providedEditor);

    const {
      markInSchema,
      isDisabled,
      isActive,
      iconDefinition,
      shortcutKey,
      formattedName,
    } = useMarkState(editor, type, disabled);

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);

        if (!e.defaultPrevented && !isDisabled && editor) {
          toggleMark(editor, type);
        }
      },
      [onClick, isDisabled, editor, type],
    );

    const show = React.useMemo(() => {
      return shouldShowMarkButton({
        editor,
        type,
        hideWhenUnavailable,
        markInSchema,
      });
    }, [editor, type, hideWhenUnavailable, markInSchema]);

    if (!show || !editor || !editor.isEditable) {
      return null;
    }

    return (
      <UnifiedButton
        type="button"
        className={className.trim()}
        disabled={isDisabled}
        data-style="ghost"
        data-active-state={isActive ? "on" : "off"}
        data-disabled={isDisabled}
        role="button"
        tabIndex={-1}
        aria-label={type}
        aria-pressed={isActive}
        tooltip={formattedName}
        shortcutKeys={shortcutKey}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        {children || (
          <>
            <FontAwesomeIcon
              icon={iconDefinition}
              className="tiptap-button-icon"
            />
            {text && <span className="tiptap-button-text">{text}</span>}
          </>
        )}
      </UnifiedButton>
    );
  },
);

MarkButton.displayName = "MarkButton";
